import React, { useEffect } from "react";

/**
 * ConfirmModal — the redesign's single confirm pattern (replaces the legacy
 * window.confirm()/alert() mix). Matches the design prototype:
 * dim overlay (click-out cancels), white radius-16 card, icon disc
 * (red for destructive actions), centred title/body, Cancel + CTA pair.
 *
 * Props:
 *   open          boolean — render nothing when false
 *   title         string/node — e.g. "Delete this order?"
 *   body          string/node — e.g. "This will permanently remove the order.
 *                 This cannot be undone."
 *   confirmLabel  string — CTA label, e.g. "Delete Order" (default "Confirm")
 *   danger        boolean — red icon disc + red CTA (destructive-confirm)
 *   onConfirm     () => void
 *   onCancel      () => void — also fired by overlay click / Escape / Cancel
 *   cancelLabel   string (optional, default "Cancel")
 *   icon          node (optional — defaults to 🗑 when danger, ✓ otherwise)
 *   busy          boolean (optional) — disables both buttons while a request
 *                 is in flight
 *
 * Usage:
 *   <ConfirmModal
 *     open={!!deletingOrder}
 *     title="Delete this order?"
 *     body="This will permanently remove the order. This cannot be undone."
 *     confirmLabel="Delete Order"
 *     danger
 *     onConfirm={confirmDelete}
 *     onCancel={() => setDeletingOrder(null)}
 *   />
 */
const ConfirmModal = ({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  icon,
  busy = false,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && onCancel) onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="adm-modal-overlay" onClick={onCancel}>
      <div
        className="adm-modal adm-modal--confirm"
        role="alertdialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={
            "adm-confirm-disc" + (danger ? "" : " adm-confirm-disc--teal")
          }
          aria-hidden="true"
        >
          {icon != null ? icon : danger ? "🗑" : "✓"}
        </div>
        <div className="adm-confirm-title">{title}</div>
        <div className="adm-confirm-body">{body}</div>
        <div className="adm-modal-actions">
          <button
            type="button"
            className="adm-btn adm-btn--soft"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={"adm-btn" + (danger ? " adm-btn--danger" : "")}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
